/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type {
  Breadcrumb,
  BrowserOptions,
  Event,
  createReduxEnhancer,
} from '@sentry/react';

export const FILTERED = '[Filtered]';

interface ReduxAction {
  type: string;
}

// Fired on every animation tick, derived from GENERIC_SETTIME which stays visible.
export const SENTRY_IGNORED_REDUX_ACTION_TYPES = new Set([
  'GENERIC_SYNC_SETTIME',
]);

export const sentryActionTransformer = (
  action: ReduxAction,
): ReduxAction | null =>
  SENTRY_IGNORED_REDUX_ACTION_TYPES.has(action.type)
    ? null
    : { type: action.type };

export const sentryReduxEnhancerOptions: Parameters<
  typeof createReduxEnhancer
>[0] = {
  attachReduxState: false,
  stateTransformer: () => null,
  actionTransformer: sentryActionTransformer,
};

const MAX_DEPTH = 12;

// Compared after lowercasing and removing "_" and "-", so refresh_token, refreshToken and Refresh-Token all match.
const SENSITIVE_KEYS = new Set([
  'token',
  'accesstoken',
  'refreshtoken',
  'idtoken',
  'authorization',
  'password',
  'codeverifier',
  'clientsecret',
  'apikey',
  'credentials',
]);

// "code" and "state" are only sensitive as OAuth query/fragment parameters, so they are not in SENSITIVE_KEYS.
const SENSITIVE_URL_PARAMS =
  /([?&#;](?:access_token|refresh_token|id_token|token|code|state|session_state|code_verifier|client_secret|password|api_key|apikey)=)[^&#\s"'<>]*/gi;

const isSensitiveKey = (key: string): boolean =>
  SENSITIVE_KEYS.has(key.toLowerCase().replace(/[-_]/g, ''));

export const redactUrlParams = (text: string): string =>
  text.replace(SENSITIVE_URL_PARAMS, `$1${FILTERED}`);

const isPlainObject = (value: object): value is Record<string, unknown> => {
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
};

const redact = (
  value: unknown,
  depth: number,
  ancestors: WeakSet<object>,
): unknown => {
  if (typeof value === 'string') {
    return redactUrlParams(value);
  }
  if (value === null || typeof value !== 'object') {
    return value;
  }
  if (!Array.isArray(value) && !isPlainObject(value)) {
    return value;
  }
  if (depth >= MAX_DEPTH) {
    return FILTERED;
  }
  if (ancestors.has(value)) {
    return '[Circular]';
  }
  ancestors.add(value);
  const result = Array.isArray(value)
    ? value.map((item) => redact(item, depth + 1, ancestors))
    : Object.fromEntries(
        Object.entries(value).map(([key, item]) => [
          key,
          isSensitiveKey(key) ? FILTERED : redact(item, depth + 1, ancestors),
        ]),
      );
  ancestors.delete(value);
  return result;
};

export const sanitizeValue = <T>(value: T): T =>
  redact(value, 0, new WeakSet()) as T;

export const sanitizeBreadcrumb = (breadcrumb: Breadcrumb): Breadcrumb =>
  sanitizeValue(breadcrumb);

export const sanitizeEvent = <T extends Event>(event: T): T => {
  const { sdkProcessingMetadata, ...sendable } = event;
  return { ...sanitizeValue(sendable), sdkProcessingMetadata } as T;
};

export const sentryPrivacyOptions: Pick<
  BrowserOptions,
  'beforeBreadcrumb' | 'beforeSend' | 'beforeSendTransaction'
> = {
  beforeBreadcrumb: sanitizeBreadcrumb,
  beforeSend: sanitizeEvent,
  beforeSendTransaction: sanitizeEvent,
};
